import React, { useState, useRef } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { Form, message, Input, Button, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { HeaderLogo, Welcome, LoginPageBackground, LoginBox, LoginSection, Login } from './styles';
import rubusLogo from '../../../images/horizontal_logo.png';
import constants from '../ChangeFirstPassword/constants';
import { API_AUTH } from '../../../commons/api';
import LoginAction from '../../../modules/actions/LoginAction';
import TranslationAction from '../../../modules/actions/TranslationAction';
import { bindActionCreators } from 'redux';

const ChangeFirstPassword = (props) => {
  const [translationCache] = useState(props.translationCache || []);
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    number: false,
    uppercase: false,
    lowercase: false,
    specialChar: false,
    common: false,
    consecutive: false
  });

  const changeFirstPwd = useRef(null);

  const handleSubmit = async (values) => {
    const passwordError = validatePassword(values.password);

    if (passwordError) {
      message.error(passwordError);
      return;
    }

    if (values.password !== values.confirmPassword) {
      message.error(translation('Passwords do not match.'));
      return;
    }

    const path = props.location.pathname.split('/changeFirstPassword/');
    const identifier = path[1]; 

    if (!identifier) {
      message.error(translation('Invalid identifier.'));
      return;
    }

    await _changePasswordAPI({ ...values, identifier });
  };

  const validatePassword = (password) => {
    const criteria = {
      length: password.length >= 8 && password.length <= 13,
      number: /\d/.test(password),
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      specialChar: /[!@#$%^&*]/.test(password),
      common: /password|123456|qwerty/i.test(password),
      consecutive: /(.)\1{2,}/.test(password),
    };

    setPasswordCriteria(criteria);

    // if (!criteria.length) return translation('Password must be 8-13 characters long.');
    // if (!criteria.number) return translation('Password must contain at least one number.');
    // if (!criteria.uppercase) return translation('Password must contain at least one uppercase letter.');
    // if (!criteria.lowercase) return translation('Password must contain at least one lowercase letter.');
    // if (!criteria.specialChar) return translation('Password must contain at least one special character.');
    // if (criteria.common) return translation('Password is too common.');
    // if (criteria.consecutive) return translation('Password must not have consecutive identical characters.');
    return '';
  };

  const _changePasswordAPI = async (data) => {
    try {
      const response = await axios.post(API_AUTH.ChangeFirstPassword, {
        siteName: data.siteName,
        oldPassword: data.oldPassword,
        password: data.password,
        confirmPassword: data.confirmPassword,
        identifier: data.identifier
      });
      message.success(response.data.message);
      props.navigate('/login');
    } catch (error) {
      console.error('API Error:', error.response ? error.response.data : error.message);
      message.error(error.response ? error.response.data.message : error.message);
    }
  };

  const translation = (keyword) => {
    return translationCache && translationCache[keyword] ? translationCache[keyword] : keyword;
  };

  const renderPasswordChecklist = () => {
    const criteriaList = [
      { text: translation('8-13 characters'), valid: passwordCriteria.length },
      { text: translation('At least one number'), valid: passwordCriteria.number },
      { text: translation('At least one uppercase letter'), valid: passwordCriteria.uppercase },
      { text: translation('At least one lowercase letter'), valid: passwordCriteria.lowercase },
      { text: translation('At least one special character'), valid: passwordCriteria.specialChar },
      { text: translation('Not a common password'), valid: !passwordCriteria.common },
      {
        text: translation('No consecutive identical characters'),
        valid: !passwordCriteria.consecutive
      }
    ];

    return (
      <ul>
        {criteriaList.map((criterion, index) => (
          <li key={index} style={{ color: criterion.valid ? 'green' : 'red' }}>
            {criterion.valid ? <CheckCircleOutlined /> : <CloseCircleOutlined />} {criterion.text}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Login>
      <LoginSection>
        <LoginPageBackground style={{ backgroundImage: `url('/covers/rubus_login.jpg')` }} />
        <LoginBox>
          <section style={{ width: '100%', textAlign: 'center' }}>
            <HeaderLogo>
              <img src={rubusLogo} alt="logo" style={{ height: '42px' }} />
            </HeaderLogo>
            <Welcome>{translation('Change Password')}</Welcome>
            <Form ref={changeFirstPwd} name="changePassword" onFinish={handleSubmit}>
              <Form.Item
                name="siteName"
                rules={[
                  { required: true, message: translation('SiteName') },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value === getFieldValue('password')) {
                        return Promise.reject(
                          translation('SiteName cannot be the same as new password.')
                        );
                      }
                      return Promise.resolve();
                    }
                  })
                ]}>
                <Input
                  style={{ width: '380px' }}
                  placeholder={translation('SiteName')}
                  onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                />
              </Form.Item>

              <Form.Item name="oldPassword">
                <Input.Password
                  style={{ width: '380px' }}
                  placeholder={translation('Current Password')}
                  onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                />
              </Form.Item>

              <Tooltip placement="bottom" title={renderPasswordChecklist()}>
                <Form.Item
                  name="password"
                  rules={[{ required: true, message: translation('New Password') }]}>
                  <Input.Password
                    style={{ width: '380px' }}
                    placeholder={translation('New Password')}
                    onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                    onChange={(e) => {
                      const passwordError = validatePassword(e.target.value);
                      if (passwordError) {
                        message.error(passwordError);
                      }
                    }}
                  />
                </Form.Item>
              </Tooltip>

              <Tooltip placement="bottom" title={renderPasswordChecklist()}>
                <Form.Item
                  name="confirmPassword"
                  rules={[{ required: true, message: translation('Confirm New Password') }]}>
                  <Input.Password
                    style={{ width: '380px' }}
                    placeholder={translation('Confirm New Password')}
                    onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                    onChange={(e) => {
                      if (e.target.value !== changeFirstPwd.current.getFieldValue('password')) {
                        message.error(translation('Passwords do not match.'));
                      }
                    }}
                  />
                </Form.Item>
              </Tooltip>

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  {translationCache && translationCache[`${constants.Save_Password}`]
                    ? translationCache[`${constants.Save_Password}`]
                    : `${constants.Save_Password}`}
                </Button>
              </Form.Item>
            </Form>
          </section>
        </LoginBox>
      </LoginSection>
    </Login>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    loginAction: bindActionCreators(LoginAction, dispatch),
    TranslationAction: bindActionCreators(TranslationAction, dispatch)
  };
};

const mapStateToProps = (state) => {
  return {
    accessToken: state.LoginReducer.accessToken
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeFirstPassword);